import React, { useCallback, useState } from "react";
import styled, { css } from "styled-components";
import { Modal, Input, ListGroup } from "components/bootstrap";
import useQuickJumpSearch from "components/quick-jump/hooks/useQuickJumpSearch";
import useQuickJumpKeyboardNavigation from "components/quick-jump/hooks/useQuickJumpKeyboardNavigation";
import Spinner from "components/common/Spinner";
import useDebouncedValue from "hooks/useDebouncedValue";
import SearchResultEntry from "./SearchResultEntry";
const SearchInput = styled(Input)`
  width: 100%;
`;
const List = styled.div`
  overflow: auto;
  outline: none;
`;
const SearchResults = ({ searchResults, highlightedIndex, getItemProps }) => /* @__PURE__ */ React.createElement(List, null, /* @__PURE__ */ React.createElement(ListGroup, { className: "no-bm" }, searchResults.map((item, index) => /* @__PURE__ */ React.createElement(
  SearchResultEntry,
  {
    key: item.key || item.title,
    item,
    isActive: highlightedIndex === index,
    itemProps: getItemProps(index),
    lastOpened: item.last_opened,
    favorite: item.favorite
  }
))));
const Explanation = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;
const Description = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.text.secondary};
    margin: 0;
  `
);
const QuickJumpIntro = () => /* @__PURE__ */ React.createElement(Explanation, null, /* @__PURE__ */ React.createElement(Description, null, "Please enter a search query to search for a page, stream, dashboard, input or other entities.", " "), /* @__PURE__ */ React.createElement(Description, null, "To give you a head start, you will find a list of your ", /* @__PURE__ */ React.createElement("strong", null, "last opened"), " and ", /* @__PURE__ */ React.createElement("strong", null, "favorite"), " ", "items below:"), /* @__PURE__ */ React.createElement("br", null));
const NoResultsFound = () => /* @__PURE__ */ React.createElement(Explanation, null, /* @__PURE__ */ React.createElement(Description, null, "No search results found. Please try different search terms."));
const SearchInputContainer = styled.div`
  display: inline-block;
  position: relative;
`;
const InputFeedback = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  min-height: 34px;
  padding-right: 3px;
`;
const QuickJumpModal = ({ onToggle }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 500);
  const { searchResults, isLoading } = useQuickJumpSearch(debouncedSearchQuery);
  const { highlightedIndex, modalProps, searchInputProps, getItemProps, onHide } = useQuickJumpKeyboardNavigation({
    items: searchResults,
    onToggle,
    searchQuery
  });
  const hasEmptySearchQuery = debouncedSearchQuery.trim() === "";
  const handleSearch = useCallback(
    (event) => {
      setSearchQuery(event.target.value);
    },
    [setSearchQuery]
  );
  return /* @__PURE__ */ React.createElement(Modal, { onHide, show: true, bsSize: "large", scrollInContent: true, rootProps: modalProps }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, "Quick Jump")), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(SearchInputContainer, null, /* @__PURE__ */ React.createElement(
    SearchInput,
    {
      value: searchQuery,
      "data-autofocus": true,
      id: "quick-jump-search",
      type: "text",
      onChange: handleSearch,
      placeholder: "Search for pages/dashboards/streams ...",
      ...searchInputProps
    }
  ), /* @__PURE__ */ React.createElement(InputFeedback, null, isLoading && /* @__PURE__ */ React.createElement(Spinner, { delay: 0, text: "" }))), !isLoading && searchResults.length === 0 ? /* @__PURE__ */ React.createElement(NoResultsFound, null) : /* @__PURE__ */ React.createElement(React.Fragment, null, hasEmptySearchQuery && /* @__PURE__ */ React.createElement(QuickJumpIntro, null), /* @__PURE__ */ React.createElement(
    SearchResults,
    {
      searchResults,
      getItemProps,
      highlightedIndex
    }
  ))));
};
export default QuickJumpModal;
